import React, { useEffect, useState } from 'react';
import { useConfirm } from '../../components/ui/useConfirm';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { warehouseService, WarehouseStockRow } from '../../services/warehouse.service';
import { useActiveWarehouses } from '../../hooks/useWarehouses';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  Field,
  FormActions,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  isQuantity,
  primaryButtonClass,
  QUANTITY_STEP,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type Tab = 'notes' | 'reasons';
type NoteStatus = 'posted' | 'cancelled';

interface ScrapNote {
  id: string;
  scrapNumber: string;
  scrapDate: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  warehouseName: string;
  quantity: number;
  unitCost: number;
  value: number;
  reasonCode: string;
  reasonName: string;
  ncrNumber: string | null;
  notes: string | null;
  status: NoteStatus;
  cancelReason: string | null;
}
interface NotePage {
  data: ScrapNote[];
  total: number;
  totalPages: number;
}
interface ScrapSummary {
  notesThisMonth: number;
  valueThisMonth: number;
  rejectedOnJobCardsThisMonth: number;
  sentToReworkThisMonth: number;
  topReason: { name: string; quantity: number } | null;
}
export interface ScrapReason {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  rejectedQuantity: number;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const STATUS: Record<NoteStatus, { label: string; className: string }> = {
  posted: { label: 'Written off', className: 'bg-red-50 text-red-700 ring-red-600/20' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-50 text-gray-600 ring-gray-500/20' },
};

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

const scrapApi = {
  page: async (params: object): Promise<NotePage> => (await api.get('/scrap-notes', { params: clean(params) })).data,
  summary: async (): Promise<ScrapSummary> => (await api.get('/scrap-notes/summary')).data,
  create: async (data: object): Promise<ScrapNote> => (await api.post('/scrap-notes', data)).data,
  cancel: async (id: string, reason: string): Promise<ScrapNote> => (await api.post(`/scrap-notes/${id}/cancel`, { reason })).data,
  reasons: async (activeOnly = false): Promise<ScrapReason[]> => (await api.get('/manufacturing/scrap-reasons', { params: activeOnly ? { activeOnly: true } : {} })).data,
  createReason: async (data: object): Promise<ScrapReason> => (await api.post('/manufacturing/scrap-reasons', data)).data,
  updateReason: async (id: string, data: object): Promise<ScrapReason> => (await api.patch(`/manufacturing/scrap-reasons/${id}`, data)).data,
  removeReason: async (id: string): Promise<void> => (await api.delete(`/manufacturing/scrap-reasons/${id}`)).data,
};

// The reasons a form offers
export const useActiveScrapReasons = () => useQuery({ queryKey: ['scrap', 'reasons', 'active'], queryFn: () => scrapApi.reasons(true), staleTime: 60_000 });

// ---------------------------------------------------------------- a scrap note

interface NcrForScrap {
  id: string;
  ncrNumber: string;
  productId: string | null;
  productName: string | null;
  quantity: number | null;
}

const NoteForm: React.FC<{ ncrId: string; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ ncrId, isPending, onCancel, onSubmit }) => {
  const { data: warehouses = [] } = useActiveWarehouses();
  const { data: reasons = [] } = useActiveScrapReasons();
  const { data: ncr } = useQuery({ queryKey: ['quality', 'ncr', ncrId], queryFn: async (): Promise<NcrForScrap> => (await api.get(`/ncrs/${ncrId}`)).data, enabled: !!ncrId });

  const [form, setForm] = useState({ scrapDate: today(), warehouseId: '', quantity: '', scrapReasonId: '', notes: '' });
  const [product, setProduct] = useState<WarehouseStockRow | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // The default warehouse to begin with
  useEffect(() => {
    if (!form.warehouseId && warehouses.length > 0) setForm((prev) => ({ ...prev, warehouseId: warehouses[0].id }));
  }, [warehouses, form.warehouseId]);

  // A note that follows from an NCR starts with its product and quantity
  useEffect(() => {
    if (!ncr) return;
    if (ncr.productName) setSearchInput((prev) => prev || (ncr.productName as string));
    if (ncr.quantity) setForm((prev) => (prev.quantity ? prev : { ...prev, quantity: String(ncr.quantity) }));
  }, [ncr]);

  const { data: stock, isFetching } = useQuery({
    queryKey: ['warehouses', 'stock', form.warehouseId, { search, limit: 8 }],
    queryFn: () => warehouseService.getStock(form.warehouseId, { search, limit: 8 }),
    enabled: !!form.warehouseId && !product && search.length >= 2,
  });
  const choices = (stock?.data ?? []).filter((row) => row.quantity > 0);

  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    // Stock is per warehouse: another warehouse means choosing the product again
    if (field === 'warehouseId') setProduct(null);
    setErrors((prev) => {
      const { [field]: _cleared, ...rest } = prev;
      return rest;
    });
  };

  const quantity = Number(form.quantity);
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!form.warehouseId) next.warehouseId = 'Choose the warehouse';
    if (!product) next.product = 'Choose the product';
    if (form.quantity.trim() === '' || !isQuantity(quantity) || quantity <= 0) next.quantity = 'Enter a quantity above zero, up to 4 decimals';
    else if (product && quantity > product.quantity) next.quantity = `Only ${formatNumber(product.quantity)} ${product.unit} in this warehouse`;
    if (!form.scrapReasonId) next.scrapReasonId = 'Choose the reason';
    if (!form.scrapDate) next.scrapDate = 'Enter the date';
    else if (form.scrapDate > today()) next.scrapDate = 'Not in the future';
    setErrors(next);
    if (Object.keys(next).length > 0 || !product) return;
    onSubmit({
      scrapDate: form.scrapDate,
      productId: product.productId,
      warehouseId: form.warehouseId,
      quantity,
      scrapReasonId: form.scrapReasonId,
      ncrId: ncrId || null,
      notes: form.notes.trim() || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      {ncr && (
        <p className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200">
          For {ncr.ncrNumber}
          {ncr.productName ? ` · ${ncr.productName}` : ''}. The note will carry the report number.
        </p>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Date" htmlFor="sn-date" required error={errors.scrapDate}>
          <input id="sn-date" type="date" max={today()} value={form.scrapDate} onChange={set('scrapDate')} className={cn(inputClass, errors.scrapDate && inputErrorClass)} />
        </Field>
        <Field label="Warehouse" htmlFor="sn-warehouse" required error={errors.warehouseId}>
          <select id="sn-warehouse" value={form.warehouseId} onChange={set('warehouseId')} className={cn(inputClass, errors.warehouseId && inputErrorClass)}>
            <option value="">Choose…</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Product" htmlFor="sn-product" required error={errors.product} className="sm:col-span-2">
          {product ? (
            <div className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2 ring-1 ring-gray-200">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{product.name}</p>
                <p className="text-xs text-gray-500">
                  {product.sku} · {formatNumber(product.quantity)} {product.unit} in this warehouse · cost {formatMoney(product.costPrice)}
                </p>
              </div>
              <button type="button" onClick={() => setProduct(null)} className="shrink-0 text-xs font-semibold text-indigo-600 hover:text-indigo-500">
                Change
              </button>
            </div>
          ) : (
            <div>
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  id="sn-product"
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  disabled={!form.warehouseId}
                  placeholder="Search product or SKU in this warehouse"
                  className={cn(inputClass, 'pl-9', errors.product && inputErrorClass)}
                />
              </div>
              {search.length >= 2 && (
                <ul className="mt-1 max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                  {choices.length === 0 ? (
                    <li className="px-3 py-2 text-sm text-gray-500">{isFetching ? 'Searching…' : 'Nothing in this warehouse matches'}</li>
                  ) : (
                    choices.map((row) => (
                      <li key={row.productId}>
                        <button
                          type="button"
                          onClick={() => {
                            setProduct(row);
                            setErrors((prev) => {
                              const { product: _cleared, ...rest } = prev;
                              return rest;
                            });
                          }}
                          className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-indigo-50"
                        >
                          <span className="min-w-0 truncate">
                            <span className="font-medium text-gray-900">{row.name}</span> <span className="text-gray-500">{row.sku}</span>
                          </span>
                          <span className="shrink-0 text-gray-600 tabular-nums">
                            {formatNumber(row.quantity)} {row.unit}
                          </span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          )}
        </Field>

        <Field label="Quantity" htmlFor="sn-quantity" required error={errors.quantity} hint={product && quantity > 0 && isQuantity(quantity) ? `Value written off: ${formatMoney(quantity * product.costPrice)}` : undefined}>
          <input id="sn-quantity" type="number" min="0" step={QUANTITY_STEP} value={form.quantity} onChange={set('quantity')} className={cn(inputClass, 'text-right tabular-nums', errors.quantity && inputErrorClass)} />
        </Field>
        <Field label="Reason" htmlFor="sn-reason" required error={errors.scrapReasonId} hint={reasons.length === 0 ? 'Add a reason on the Reasons tab first' : undefined}>
          <select id="sn-reason" value={form.scrapReasonId} onChange={set('scrapReasonId')} className={cn(inputClass, errors.scrapReasonId && inputErrorClass)}>
            <option value="">Choose…</option>
            {reasons.map((reason) => (
              <option key={reason.id} value={reason.id}>
                {reason.code} · {reason.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Notes" htmlFor="sn-notes" className="sm:col-span-2">
          <input id="sn-notes" type="text" maxLength={500} value={form.notes} onChange={set('notes')} className={inputClass} />
        </Field>
      </div>
      <p className="text-xs text-gray-500">The stock leaves the warehouse as soon as the note is saved. A note saved by mistake can be cancelled, which puts the stock back.</p>
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : 'Write Off Stock'}
        </button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- a reason

const ReasonForm: React.FC<{ reason: ScrapReason | null; isPending: boolean; onCancel: () => void; onSubmit: (data: { code: string; name: string; isActive: boolean }) => void }> = ({ reason, isPending, onCancel, onSubmit }) => {
  const [form, setForm] = useState({ code: reason?.code ?? '', name: reason?.name ?? '', isActive: reason?.isActive ?? true });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    const code = form.code.trim().toUpperCase();
    if (!reason && !/^[A-Z0-9][A-Z0-9-]{1,19}$/.test(code)) next.code = '2 to 20 capitals, digits or hyphens';
    if (form.name.trim().length < 2) next.name = 'Name the reason';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({ code, name: form.name.trim(), isActive: form.isActive });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <Field label="Code" htmlFor="sr-code" required={!reason} error={errors.code} hint={reason ? 'The code cannot change' : 'Short and unique, such as DIM'}>
        <input id="sr-code" type="text" maxLength={20} disabled={!!reason} value={form.code} onChange={(event) => setForm((prev) => ({ ...prev, code: event.target.value.toUpperCase() }))} className={cn(inputClass, 'uppercase', errors.code && inputErrorClass)} />
      </Field>
      <Field label="Reason" htmlFor="sr-name" required error={errors.name}>
        <input id="sr-name" type="text" maxLength={100} value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} className={cn(inputClass, errors.name && inputErrorClass)} placeholder="Dimension out of tolerance" />
      </Field>
      {reason && (
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input id="sr-active" type="checkbox" checked={form.isActive} onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
          In use: offered on job cards and scrap notes
        </label>
      )}
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : reason ? 'Save Reason' : 'Add Reason'}
        </button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- the page

const Scrap: React.FC = () => {
  const [ask, confirmDialog] = useConfirm();
  const { can } = usePermissions();
  const canWriteOff = can('inventory.create');
  const canCancel = can('inventory.update');
  const canAddReason = can('manufacturing.create');
  const canEditReason = can('manufacturing.update');
  const canDeleteReason = can('manufacturing.delete');
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const ncrFromLink = searchParams.get('ncr') ?? '';

  const [tab, setTab] = useState<Tab>('notes');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<NoteStatus | ''>('');
  const [noteOpen, setNoteOpen] = useState(!!ncrFromLink);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [editingReason, setEditingReason] = useState<ScrapReason | null>(null);
  const [cancelling, setCancelling] = useState<ScrapNote | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['scrap', 'notes', { page, limit, search, status: statusFilter }],
    queryFn: () => scrapApi.page({ page, limit, search, status: statusFilter }),
    placeholderData: keepPreviousData,
  });
  const { data: summary } = useQuery({ queryKey: ['scrap', 'summary'], queryFn: scrapApi.summary });
  const reasonsQuery = useQuery({ queryKey: ['scrap', 'reasons', 'all'], queryFn: () => scrapApi.reasons(false) });

  // A write-off changes stock everywhere it is shown
  const refresh = () => ['scrap', 'warehouses', 'inventory', 'products'].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
  const closeNote = () => {
    setNoteOpen(false);
    if (ncrFromLink) setSearchParams({}, { replace: true });
  };
  const closeReason = () => {
    setReasonOpen(false);
    setEditingReason(null);
  };

  const createNote = useMutation({
    mutationFn: scrapApi.create,
    onSuccess: (note) => {
      toast.success(`${note.scrapNumber}: ${formatNumber(note.quantity)} ${note.unit} written off`);
      refresh();
      closeNote();
    },
    onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The stock could not be written off')),
  });
  const cancelNote = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => scrapApi.cancel(id, reason),
    onSuccess: (note) => {
      toast.success(`${note.scrapNumber} cancelled; the stock is back`);
      refresh();
      setCancelling(null);
      setCancelReason('');
    },
    onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The scrap note could not be cancelled')),
  });
  const saveReason = useMutation({
    mutationFn: (input: { code: string; name: string; isActive: boolean }) =>
      editingReason ? scrapApi.updateReason(editingReason.id, { name: input.name, isActive: input.isActive }) : scrapApi.createReason({ code: input.code, name: input.name }),
    onSuccess: (reason) => {
      toast.success(`${reason.code} saved`);
      refresh();
      closeReason();
    },
    onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The reason could not be saved')),
  });
  const removeReason = useMutation({
    mutationFn: (reason: ScrapReason) => scrapApi.removeReason(reason.id),
    onSuccess: () => {
      toast.success('Reason deleted');
      refresh();
    },
    onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The reason could not be deleted')),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const hasFilters = !!search || !!statusFilter;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const reasons = (reasonsQuery.data ?? []).filter((reason) => !search || `${reason.code} ${reason.name}`.toLowerCase().includes(search.toLowerCase()));

  const stats = [
    { label: 'Written off this month', value: formatMoney(summary?.valueThisMonth ?? 0), hint: `${formatNumber(summary?.notesThisMonth ?? 0)} scrap note(s), at cost price` },
    { label: 'Rejected on job cards', value: formatNumber(summary?.rejectedOnJobCardsThisMonth ?? 0), hint: 'Pieces this month; they never came into stock' },
    { label: 'Sent to rework', value: formatNumber(summary?.sentToReworkThisMonth ?? 0), hint: 'Pieces this month' },
    { label: 'Top reason', value: summary?.topReason?.name ?? '—', hint: summary?.topReason ? `${formatNumber(summary.topReason.quantity)} this month, job cards and notes together` : 'Nothing scrapped this month' },
  ];

  return (
    <div className="space-y-6">
      {confirmDialog}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Scrap</h1>
          <p className="mt-1 text-sm text-gray-500">Stock written off as scrap, and the list of reasons used here and on job cards.</p>
        </div>
        {tab === 'notes'
          ? canWriteOff && (
              <button type="button" onClick={() => setNoteOpen(true)} className={primaryButtonClass}>
                <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
                New Scrap Note
              </button>
            )
          : canAddReason && (
              <button type="button" onClick={() => setReasonOpen(true)} className={primaryButtonClass}>
                <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
                Add Reason
              </button>
            )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 truncate text-2xl font-semibold text-gray-900" title={String(stat.value)}>{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex gap-1 border-b border-gray-200 px-4 pt-3" role="tablist">
          {([{ key: 'notes', label: 'Scrap notes' }, { key: 'reasons', label: 'Reasons' }] as { key: Tab; label: string }[]).map((item) => (
            <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} onClick={() => { setTab(item.key); setPage(1); setSearchInput(''); setSearch(''); }} className={cn('-mb-px border-b-2 px-3 py-2 text-sm font-medium', tab === item.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              {item.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder={tab === 'notes' ? 'Search by number, product, SKU, reason or NCR' : 'Search by code or reason'} aria-label="Search" className={cn(inputClass, 'pl-9')} />
          </div>
          {tab === 'notes' && (
            <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as NoteStatus | ''); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-44')}>
              <option value="">All statuses</option>
              {(Object.keys(STATUS) as NoteStatus[]).map((status) => (<option key={status} value={status}>{STATUS[status].label}</option>))}
            </select>
          )}
        </div>

        {tab === 'notes' ? (
          <>
            <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className={thClass}>Scrap note</th>
                    <th scope="col" className={thClass}>Product</th>
                    <th scope="col" className={thClass}>Warehouse</th>
                    <th scope="col" className={cn(thClass, 'text-right')}>Quantity</th>
                    <th scope="col" className={cn(thClass, 'text-right')}>Value</th>
                    <th scope="col" className={thClass}>Reason</th>
                    <th scope="col" className={thClass}>Status</th>
                    <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {isError ? (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The scrap notes could not be loaded.')}</td></tr>
                  ) : isLoading ? (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center">
                        <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No scrap note matches' : 'No scrap notes yet'}</p>
                        <p className="mt-1 text-sm text-gray-500">Write stock off here when it is damaged, spoiled or found unusable. Pieces rejected on a job card need no note.</p>
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">{row.scrapNumber}</p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            {formatDate(row.scrapDate)}
                            {row.ncrNumber ? ` · ${row.ncrNumber}` : ''}
                          </p>
                        </td>
                        <td className="max-w-xs px-4 py-3">
                          <p className="truncate text-sm text-gray-900">{row.productName}</p>
                          <p className="mt-0.5 text-xs text-gray-500">{row.sku}</p>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.warehouseName}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">
                          {formatNumber(row.quantity)} <span className="font-normal text-gray-500">{row.unit}</span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatMoney(row.value)}</td>
                        <td className="max-w-xs px-4 py-3">
                          <p className="truncate text-sm text-gray-700">{row.reasonName}</p>
                          {(row.cancelReason || row.notes) && <p className="mt-0.5 truncate text-xs text-gray-500" title={row.cancelReason ?? row.notes ?? ''}>{row.cancelReason ? `Cancelled: ${row.cancelReason}` : row.notes}</p>}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATUS[row.status].className)}>{STATUS[row.status].label}</span></td>
                        <td className="whitespace-nowrap px-4 py-3 text-right">
                          {row.status === 'posted' && canCancel ? (
                            <button type="button" onClick={() => setCancelling(row)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-50" aria-label={`Cancel ${row.scrapNumber}`}>
                              Cancel
                            </button>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
                <label className="flex items-center gap-2">
                  Rows per page
                  <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                    {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
                  </select>
                </label>
              </div>
              {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
            </div>
          </>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Code</th>
                  <th scope="col" className={thClass}>Reason</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Rejected on job cards</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reasonsQuery.isError ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(reasonsQuery.error, 'The reasons could not be loaded.')}</td></tr>
                ) : reasonsQuery.isLoading ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
                ) : reasons.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center">
                      <p className="text-sm font-medium text-gray-900">{search ? 'No reason matches' : 'No scrap reasons yet'}</p>
                      <p className="mt-1 text-sm text-gray-500">Add the handful of reasons the plant uses, so that scrap can be added up by cause.</p>
                    </td>
                  </tr>
                ) : (
                  reasons.map((reason) => (
                    <tr key={reason.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{reason.code}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{reason.name}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{reason.rejectedQuantity > 0 ? formatNumber(reason.rejectedQuantity) : '—'}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className={cn(badgeClass, reason.isActive ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-gray-50 text-gray-600 ring-gray-500/20')}>{reason.isActive ? 'In use' : 'Switched off'}</span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          {canEditReason && (
                            <button type="button" onClick={() => { setEditingReason(reason); setReasonOpen(true); }} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Edit ${reason.code}`}>
                              Edit
                            </button>
                          )}
                          {canDeleteReason && reason.rejectedQuantity === 0 && (
                            <button type="button" onClick={() => ask({ title: `Delete the reason ${reason.code}?`, message: `${reason.name} has not been used yet, so nothing else changes.`, confirmLabel: 'Delete' }, () => removeReason.mutate(reason))} disabled={removeReason.isPending} className="rounded-md px-2.5 py-1 text-xs font-semibold text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-50 disabled:opacity-50" aria-label={`Delete ${reason.code}`}>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={noteOpen} onClose={closeNote} title="New Scrap Note" description="Stock that is damaged, spoiled or unusable, written off from one warehouse." size="lg">
        {noteOpen && <NoteForm ncrId={ncrFromLink} isPending={createNote.isPending} onCancel={closeNote} onSubmit={(input) => createNote.mutate(input)} />}
      </Modal>

      <Modal isOpen={reasonOpen} onClose={closeReason} title={editingReason ? `Edit ${editingReason.code}` : 'Add Scrap Reason'} size="md">
        {reasonOpen && <ReasonForm reason={editingReason} isPending={saveReason.isPending} onCancel={closeReason} onSubmit={(input) => saveReason.mutate(input)} />}
      </Modal>

      <Modal isOpen={!!cancelling} onClose={() => { setCancelling(null); setCancelReason(''); }} title={cancelling ? `Cancel ${cancelling.scrapNumber}` : 'Cancel Scrap Note'} size="md">
        {cancelling && (
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              if (cancelReason.trim().length >= 3) cancelNote.mutate({ id: cancelling.id, reason: cancelReason.trim() });
            }}
            className="space-y-4"
          >
            <p className="text-sm text-gray-700">
              {formatNumber(cancelling.quantity)} {cancelling.unit} of {cancelling.productName} go back into {cancelling.warehouseName}.
            </p>
            <Field label="Why is it cancelled?" htmlFor="sn-cancel-reason" required>
              <input id="sn-cancel-reason" type="text" maxLength={300} value={cancelReason} onChange={(event) => setCancelReason(event.target.value)} className={inputClass} />
            </Field>
            <FormActions>
              <button type="button" onClick={() => { setCancelling(null); setCancelReason(''); }} className={secondaryButtonClass}>
                Keep the Note
              </button>
              <button type="submit" disabled={cancelNote.isPending || cancelReason.trim().length < 3} className={dangerButtonClass}>
                {cancelNote.isPending ? 'Cancelling…' : 'Cancel Scrap Note'}
              </button>
            </FormActions>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default Scrap;
